import { useEffect, useRef, useState } from 'react';
import { TuningSlide } from '@/components/tuner/TuningSlide';
import { RotateCcw, Check, Pencil, Plus, Share2, Play, Square } from 'lucide-react';
import { SCALES, DEGREE_LABELS } from '@/lib/scales';
import { pitchClass } from '@/lib/pitch';
import { ScaleEditor } from '@/components/tuner/ScaleEditor';
import QRShareModal from '@/components/QRShareModal';
import { buildExerciseShareUrl } from '@/lib/tuningShare';

const HIT_CENTS = 10;
const HOLD_MS = 250;
const iconBtn = 'h-11 w-11 shrink-0 rounded-xl border border-white/10 flex items-center justify-center hover:bg-white/5 active:scale-95 transition-transform';

const DegreeChip = ({ s, rootPc, flats, hit, current }) => (
  <div
    data-testid={`tuner-scale-degree-${s}`}
    data-hit={hit}
    className={`relative h-16 rounded-xl border flex flex-col items-center justify-center transition-[background-color,border-color,box-shadow] duration-200 ${
      hit ? 'bg-emerald-400/15 border-emerald-400/70 text-emerald-300 shadow-[0_0_18px_rgba(52,211,153,0.35)]'
        : current ? 'border-sky-400/60 text-sky-200' : 'border-white/10 text-white/70'
    }`}
  >
    <span className="font-bebas text-2xl leading-none">{pitchClass((rootPc + s) % 12, flats)}</span>
    <span className="text-[9px] font-mono opacity-60 mt-0.5">{DEGREE_LABELS[s]}</span>
    {hit && <Check className="absolute top-1 right-1 w-3 h-3" />}
  </div>
);

// Scale degrees over the drone; lights each degree once played in tune.
export const ScaleTracker = ({ drone, interval, pitch, flats, scales, running, onRunningChange }) => {
  const { scale, selectedId, select, custom, save, remove } = scales;
  const [hits, setHits] = useState(() => new Set());
  const [editor, setEditor] = useState(null);
  const [sharing, setSharing] = useState(false);
  const holdRef = useRef({ degree: null, since: 0 });
  // With a chord sequence the scale stays on its key, not on the chord of the moment.
  const rootPc = drone.progOn ? drone.cfg.pc : drone.rootPc;
  const root = `${pitchClass(rootPc, flats)}`;

  const reset = () => { setHits(new Set()); holdRef.current = { degree: null, since: 0 }; };
  useEffect(reset, [selectedId, rootPc, scale?.steps.join()]);

  // Against the drone when it plays, otherwise the played note vs the scale's tonic.
  const played = drone.active && !drone.progOn && interval ? { degree: interval.degree, cents: interval.cents }
    : pitch ? { degree: (pitch.pc - rootPc + 12) % 12, cents: pitch.cents } : null;

  useEffect(() => {
    const h = holdRef.current;
    if (!running || !scale || !played || Math.abs(played.cents) > HIT_CENTS || !scale.steps.includes(played.degree)) {
      h.degree = null;
      return;
    }
    const now = performance.now();
    if (h.degree !== played.degree) { h.degree = played.degree; h.since = now; return; }
    if (now - h.since >= HOLD_MS && !hits.has(played.degree)) setHits((prev) => new Set(prev).add(played.degree));
  }, [played?.degree, played?.cents, pitch, running, scale, hits]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = () => { onRunningChange(true); if (!drone.active) drone.toggle(); };
  const stopAll = () => { onRunningChange(false); if (drone.active) drone.toggle(); };

  const done = scale && scale.steps.every((s) => hits.has(s));

  return (
    <section data-testid="tuner-scale" className="bg-[#111112] border border-white/5 rounded-2xl p-4 sm:p-6 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">Scala sul bordone</span>
        {scale && (
          <span data-testid="tuner-scale-progress" className={`text-xs font-mono ${done ? 'text-emerald-300' : 'text-white/50'}`}>
            {done ? 'Completata!' : `${hits.size}/${scale.steps.length}`}
          </span>
        )}
      </div>
      {scale && running && <TuningSlide pitch={pitch} testid="scale-tuning-slide" />}
      <div className="flex gap-2">
        <select
          data-testid="tuner-scale-select"
          value={selectedId}
          onChange={(e) => select(e.target.value)}
          className="h-11 min-w-0 flex-1 rounded-xl bg-[#0A0A0A] border border-white/10 px-3 text-sm text-white focus:outline-none focus:border-pink-400"
        >
          <option value="off">Nessuna scala</option>
          <optgroup label="Preset">
            {SCALES.map((s) => <option key={s.id} value={s.id}>{`${root} ${s.name}`}</option>)}
          </optgroup>
          {custom.length > 0 && (
            <optgroup label="Personali">
              {custom.map((s) => <option key={s.id} value={s.id}>{`${root} ${s.name}`}</option>)}
            </optgroup>
          )}
        </select>
        {scale?.custom && <button data-testid="tuner-scale-edit" aria-label="Modifica scala" onClick={() => setEditor(scale)} className={iconBtn}><Pencil className="w-4 h-4" /></button>}
        <button data-testid="tuner-scale-new" aria-label="Nuova scala" onClick={() => setEditor('new')} className={`${iconBtn} text-pink-300 border-pink-400/40`}><Plus className="w-4 h-4" /></button>
      </div>
      {scale && (
        <>
          <div className="grid gap-1.5 sm:gap-2" style={{ gridTemplateColumns: `repeat(${scale.steps.length > 8 ? Math.ceil(scale.steps.length / 2) : scale.steps.length}, minmax(0, 1fr))` }}>
            {scale.steps.map((s) => (
              <DegreeChip key={s} s={s} rootPc={rootPc} flats={flats} hit={hits.has(s)} current={running && played?.degree === s} />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {running ? (
              <button data-testid="tuner-scale-stop" onClick={stopAll} className="h-9 px-4 rounded-full bg-sky-400 text-black text-[10px] font-semibold uppercase tracking-[0.2em] inline-flex items-center gap-1.5 active:scale-95 transition-transform">
                <Square className="w-3.5 h-3.5" /> Stop
              </button>
            ) : (
              <button data-testid="tuner-scale-play" onClick={play} className="h-9 px-4 rounded-full bg-pink-400 text-black text-[10px] font-semibold uppercase tracking-[0.2em] inline-flex items-center gap-1.5 active:scale-95 transition-transform">
                <Play className="w-3.5 h-3.5" /> Play
              </button>
            )}
            <button data-testid="tuner-scale-reset" onClick={reset} className="h-9 px-3 rounded-full border border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/60 hover:bg-white/5 inline-flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Ricomincia
            </button>
            <button data-testid="tuner-scale-share" onClick={() => setSharing(true)} className="h-9 px-3 rounded-full border border-sky-400/40 text-[10px] uppercase tracking-[0.2em] text-sky-300 hover:bg-sky-400/10 inline-flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5" /> Condividi esercizio
            </button>
          </div>
        </>
      )}
      {scale && <span data-testid="tuner-scale-hint" className="text-[11px] text-white/40">{!running ? 'Premi Play per ascoltare dal microfono (avvia anche il bordone).' : drone.active ? 'Suona le note della scala sul bordone: ogni nota intonata (±10 cent) si accende.' : 'Bordone spento: le note vengono riconosciute rispetto alla tonica della scala.'}</span>}
      <ScaleEditor
        open={editor !== null}
        initial={editor === 'new' ? null : editor}
        rootPc={rootPc}
        flats={flats}
        onClose={() => setEditor(null)}
        onSave={(s) => { save(s); setEditor(null); }}
        onDelete={(id) => { remove(id); setEditor(null); }}
      />
      <QRShareModal
        open={sharing && !!scale}
        url={sharing && scale ? buildExerciseShareUrl(drone.cfg, scale) : ''}
        title={`${root} ${scale?.name || ''}`}
        subtitle={`Bordone ${root}${drone.cfg.octave}${drone.cfg.fifth ? ' + quinta' : ''} · ${drone.cfg.temperament === 'just' ? 'Intonazione naturale' : 'Temperamento equabile'}`}
        caption="Scansiona per aprire l'esercizio di scala"
        onClose={() => setSharing(false)}
      />
    </section>
  );
};
